import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Link,
  SimpleGrid,
  HStack,
  Tooltip,
  Button,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { FiUsers, FiAlertTriangle, FiAlertCircle, FiMinusCircle, FiFilter, FiSend } from 'react-icons/fi';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { BiGridAlt } from 'react-icons/bi';
import SmartFilterBar from 'components/SmartFilterBar';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';
import { downloadExcel } from 'react-export-table-to-excel';

const PAGE_SIZE = 10;

const REQ_STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'ready', label: 'Al día' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'incomplete', label: 'Incompletos' },
  { value: 'denied', label: 'Con denegados' },
];

const BRAND_BUTTON = '#574feb';

const DEFAULT_MENSAJE_INICIAL = `Estimado/a acudiente, reciba un cordial saludo.
El presente comunicado tiene como objetivo informarle sobre el estado de los requisitos documentales del estudiante. Es importante que los documentos listados a continuación sean completados a la brevedad posible para garantizar la correcta vinculación del estudiante al programa académico.`;

const DEFAULT_MENSAJE_FINAL = `Agradecemos su atención y colaboración. Para cualquier inquietud o información adicional, la Coordinación de Bienestar Estudiantil se encuentra a su disposición.`;

function getInitials(student) {
  const u = student?.user || {};
  const first = (u.first_name || '').trim().charAt(0) || '';
  const last = (u.last_name || '').trim().charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getFullName(student) {
  const u = student?.user || {};
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
}

function truncateLabel(s, max = 22) {
  if (!s) return '—';
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function statusToExcelLabel(status, approvedPreviousYear) {
  if (approvedPreviousYear) return 'Año anterior';
  if (status === 'approved') return 'Aprobado';
  if (status === 'pending') return 'Pendiente';
  if (status === 'denied') return 'Denegado';
  if (status === 'missing') return 'Faltante';
  if (status === 'not_applicable') return 'No aplica';
  return status || '—';
}

function RequirementCell({ status, approvedPreviousYear }) {
  if (approvedPreviousYear) {
    return (
      <Flex justify="center">
        <Box
          bg="purple.100"
          borderRadius="lg"
          p={2}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          title="Aprobado año anterior"
        >
          <Box as={IoIosCheckmarkCircleOutline} color="purple.600" boxSize={6} />
        </Box>
      </Flex>
    );
  }
  if (status === 'approved') {
    return (
      <Flex justify="center">
        <Box
          bg="green.100"
          borderRadius="lg"
          p={2}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          title="Aprobado"
        >
          <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
        </Box>
      </Flex>
    );
  }
  if (status === 'denied') {
    return (
      <Flex justify="center">
        <Box
          bg="red.100"
          borderRadius="lg"
          p={2}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          title="Denegado"
        >
          <Box as={FiAlertTriangle} color="red.600" boxSize={6} />
        </Box>
      </Flex>
    );
  }
  if (status === 'pending') {
    return (
      <Flex justify="center">
        <Box
          bg="orange.100"
          borderRadius="lg"
          p={2}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          title="Pendiente"
        >
          <Box as={FiAlertCircle} color="orange.600" boxSize={6} />
        </Box>
      </Flex>
    );
  }
  if (status === 'missing') {
    return (
      <Flex justify="center">
        <Box w={2} h={2} borderRadius="full" bg="gray.300" title="Faltante" />
      </Flex>
    );
  }
  return <Box />;
}

export default function ReporteRequisitos() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => ({
    program_type: searchParams.get('program_type') || '',
    program: searchParams.get('program') || '',
    group: searchParams.get('group') || '',
    status: searchParams.get('status') || '',
    semester: searchParams.get('semester') || '',
  }));
  const [reqStatus, setReqStatus] = useState(() => searchParams.get('req_status') || 'all');
  const [report, setReport] = useState({ data: [], meta: null });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    const n = parseInt(p, 10);
    return n >= 1 ? n : 1;
  });
  const toast = useToast();

  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();
  const [emailStudent, setEmailStudent] = useState(null);
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [emailAsunto, setEmailAsunto] = useState('');
  const [emailMensajeInicial, setEmailMensajeInicial] = useState(DEFAULT_MENSAJE_INICIAL);
  const [emailMensajeFinal, setEmailMensajeFinal] = useState(DEFAULT_MENSAJE_FINAL);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailLoadingPreview, setEmailLoadingPreview] = useState(false);
  const [emailLoadingSend, setEmailLoadingSend] = useState(false);
  const emailPreviewDebounceRef = useRef(null);
  const emailLatestRef = useRef({ mensajeInicial: '', mensajeFinal: '' });

  const { isOpen: isBulkEmailOpen, onOpen: onBulkEmailOpen, onClose: onBulkEmailClose } = useDisclosure();
  const [bulkEmailStep, setBulkEmailStep] = useState('configuring');
  const [bulkEmailAsunto, setBulkEmailAsunto] = useState('');
  const [bulkEmailMensajeInicial, setBulkEmailMensajeInicial] = useState(DEFAULT_MENSAJE_INICIAL);
  const [bulkEmailMensajeFinal, setBulkEmailMensajeFinal] = useState(DEFAULT_MENSAJE_FINAL);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [bulkEmailResult, setBulkEmailResult] = useState(null);
  const [bulkEmailError, setBulkEmailError] = useState(null);

  const hasMinimumFilters = Boolean(filters.program_type);

  const syncToUrl = useCallback(
    (nextFilters, nextReqStatus, page) => {
      const next = new URLSearchParams();
      if (nextFilters.program_type) next.set('program_type', nextFilters.program_type);
      if (nextFilters.program) next.set('program', nextFilters.program);
      if (nextFilters.group) next.set('group', nextFilters.group);
      if (nextFilters.status) next.set('status', nextFilters.status);
      if (nextFilters.semester) next.set('semester', nextFilters.semester);
      if (nextReqStatus && nextReqStatus !== 'all') next.set('req_status', nextReqStatus);
      if (page > 1) next.set('page', String(page));
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const loadReport = useCallback(async () => {
    if (!hasMinimumFilters) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        program_type: filters.program_type,
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
      });
      if (filters.program) params.set('program', filters.program);
      if (filters.group) params.set('group', filters.group);
      if (filters.status) params.set('status', filters.status);
      if (filters.semester) params.set('semester', filters.semester);
      if (reqStatus && reqStatus !== 'all') params.set('req_status', reqStatus);

      const res = await xhr(`/students/requirements-report?${params.toString()}`);
      const body = res.data || res;
      setReport({ data: body.data || [], meta: body.meta || null });
    } catch (err) {
      console.error('Error loading requirements report:', err);
      setReport({ data: [], meta: null });
    } finally {
      setLoading(false);
    }
  }, [hasMinimumFilters, filters, currentPage, reqStatus]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleFilterChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1);
      syncToUrl(newFilters, reqStatus, 1);
    },
    [syncToUrl, reqStatus]
  );

  const handleReqStatusChange = useCallback(
    (e) => {
      const v = e.target.value;
      setReqStatus(v);
      setCurrentPage(1);
      syncToUrl(filters, v, 1);
    },
    [filters, syncToUrl]
  );

  const handlePageChange = useCallback(
    (event) => {
      const page = event.selected + 1;
      setCurrentPage(page);
      syncToUrl(filters, reqStatus, page);
    },
    [filters, reqStatus, syncToUrl]
  );

  const handleDownloadExcel = useCallback(async () => {
    if (!hasMinimumFilters) return;
    setDownloading(true);
    try {
      const baseParams = new URLSearchParams({
        program_type: filters.program_type,
        page: '1',
        pageSize: '100',
      });
      if (filters.program) baseParams.set('program', filters.program);
      if (filters.group) baseParams.set('group', filters.group);
      if (filters.status) baseParams.set('status', filters.status);
      if (filters.semester) baseParams.set('semester', filters.semester);
      if (reqStatus && reqStatus !== 'all') baseParams.set('req_status', reqStatus);

      const first = await xhr(`/students/requirements-report?${baseParams.toString()}`);
      const firstBody = first.data || first;
      const total = firstBody.meta?.pagination?.total ?? 0;
      const pageSize = 100;
      const pageCount = Math.ceil(total / pageSize) || 1;

      let allRows = [...(firstBody.data || [])];
      const reqCols = firstBody.meta?.requirements || [];

      for (let page = 2; page <= pageCount; page++) {
        baseParams.set('page', String(page));
        const res = await xhr(`/students/requirements-report?${baseParams.toString()}`);
        const data = (res.data || res).data || [];
        allRows = allRows.concat(data);
      }

      const header = [
        'ID',
        'NOMBRES',
        'CELULAR',
        'CORREO',
        ...reqCols.map((r) => r.name || `Req ${r.id}`),
      ];

      const body = allRows.map((row) => {
        const u = row.student?.user || {};
        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
        const byId = {};
        const prevYearById = {};
        (row.requirements || []).forEach((x) => {
          byId[x.requirementId] = x.status;
          prevYearById[x.requirementId] = x.approvedPreviousYear || false;
        });
        const cells = reqCols.map((r) => statusToExcelLabel(byId[r.id], prevYearById[r.id]));
        return [row.student?.id ?? '—', fullName, u.cellphone ?? '—', u.email ?? '—', ...cells];
      });

      downloadExcel({
        fileName: `reporte-requisitos-${dayjs().format('YYYY-MM-DD')}`,
        sheet: 'requisitos',
        tablePayload: { header, body },
      });
    } catch (err) {
      console.error('Error descargando excel reporte requisitos:', err);
    } finally {
      setDownloading(false);
    }
  }, [hasMinimumFilters, filters, reqStatus]);

  useEffect(() => {
    emailLatestRef.current = {
      mensajeInicial: emailMensajeInicial,
      mensajeFinal: emailMensajeFinal,
    };
  }, [emailMensajeInicial, emailMensajeFinal]);

  const fetchEmailPreview = useCallback(async () => {
    if (!emailStudent) return;
    const latest = emailLatestRef.current;
    const snapshot = { mensajeInicial: latest.mensajeInicial, mensajeFinal: latest.mensajeFinal };
    setEmailLoadingPreview(true);
    try {
      const res = await xhr.post('/students/preview-requirements-report-email', {
        studentId: emailStudent.student?.id,
        mensajeInicial: latest.mensajeInicial,
        mensajeFinal: latest.mensajeFinal,
        studentName: getFullName(emailStudent.student),
        requirements: emailStudent.requirements || [],
      });
      const data = res?.data ?? res;
      const current = emailLatestRef.current;
      if (current.mensajeInicial === snapshot.mensajeInicial && current.mensajeFinal === snapshot.mensajeFinal) {
        setEmailPreviewHtml(data.html || '');
      }
      if (data.parentEmail != null) {
        setEmailDestinatario(data.parentEmail);
      }
    } catch (err) {
      console.error('Error generando vista previa:', err);
      const current = emailLatestRef.current;
      if (current.mensajeInicial === snapshot.mensajeInicial && current.mensajeFinal === snapshot.mensajeFinal) {
        setEmailPreviewHtml('');
      }
    } finally {
      setEmailLoadingPreview(false);
    }
  }, [emailStudent]);

  const scheduleEmailPreview = useCallback(() => {
    if (emailPreviewDebounceRef.current) clearTimeout(emailPreviewDebounceRef.current);
    emailPreviewDebounceRef.current = setTimeout(() => {
      fetchEmailPreview();
    }, 400);
  }, [fetchEmailPreview]);

  const handleOpenEmail = useCallback(
    (row) => {
      setEmailStudent(row);
      setEmailAsunto(`Requisitos pendientes - ${getFullName(row.student)}`);
      setEmailMensajeInicial(DEFAULT_MENSAJE_INICIAL);
      setEmailMensajeFinal(DEFAULT_MENSAJE_FINAL);
      setEmailPreviewHtml('');
      setEmailDestinatario('');
      onEmailOpen();
    },
    [onEmailOpen]
  );

  const handleCloseEmail = useCallback(() => {
    onEmailClose();
    if (emailPreviewDebounceRef.current) clearTimeout(emailPreviewDebounceRef.current);
  }, [onEmailClose]);

  const handleSendEmail = useCallback(async () => {
    if (!emailStudent?.student?.id) return;
    if (!emailAsunto?.trim()) return;
    setEmailLoadingSend(true);
    try {
      await xhr.post('/students/send-requirements-report-email', {
        studentId: emailStudent.student.id,
        asunto: emailAsunto.trim(),
        destinatario: emailDestinatario?.trim() || undefined,
        mensajeInicial: emailMensajeInicial,
        mensajeFinal: emailMensajeFinal,
        studentName: getFullName(emailStudent.student),
        requirements: emailStudent.requirements || [],
      });
      toast({ title: 'Correo enviado correctamente', status: 'success', duration: 3000 });
      handleCloseEmail();
    } catch (err) {
      console.error('Error enviando correo:', err);
      toast({ title: 'Error al enviar el correo', status: 'error', duration: 4000 });
    } finally {
      setEmailLoadingSend(false);
    }
  }, [emailStudent, emailAsunto, emailDestinatario, emailMensajeInicial, emailMensajeFinal, handleCloseEmail, toast]);

  useEffect(() => {
    if (isEmailOpen && emailStudent?.student?.id) {
      fetchEmailPreview();
    }
  }, [isEmailOpen]);

  const handleBulkEmailOpen = useCallback(() => {
    setBulkEmailStep('configuring');
    setBulkEmailAsunto('Requisitos documentales pendientes - Klazia');
    setBulkEmailMensajeInicial(DEFAULT_MENSAJE_INICIAL);
    setBulkEmailMensajeFinal(DEFAULT_MENSAJE_FINAL);
    setBulkEmailResult(null);
    setBulkEmailError(null);
    onBulkEmailOpen();
  }, [onBulkEmailOpen]);

  const handleBulkEmailClose = useCallback(() => {
    onBulkEmailClose();
    setBulkEmailStep('configuring');
    setBulkEmailLoading(false);
    setBulkEmailResult(null);
    setBulkEmailError(null);
  }, [onBulkEmailClose]);

  const handleBulkEmailSend = useCallback(async () => {
    if (!bulkEmailAsunto?.trim() || !filters.program_type) return;
    setBulkEmailStep('sending');
    setBulkEmailLoading(true);
    setBulkEmailError(null);
    try {
      const res = await xhr.post('/students/bulk-send-requirements-report-email', {
        program_type: filters.program_type,
        program: filters.program || undefined,
        group: filters.group || undefined,
        semester: filters.semester || undefined,
        req_status: reqStatus || 'all',
        asunto: bulkEmailAsunto.trim(),
        mensajeInicial: bulkEmailMensajeInicial,
        mensajeFinal: bulkEmailMensajeFinal,
      });
      const data = res?.data ?? res;
      setBulkEmailResult(data);
      setBulkEmailStep('success');
      const toastParts = [`${data.sent ?? 0} enviados`];
      if (data.skippedAllApproved > 0) toastParts.push(`${data.skippedAllApproved} al día (omitidos)`);
      if (data.noEmail > 0) toastParts.push(`${data.noEmail} sin correo`);
      toast({ title: toastParts.join(', '), status: 'success', duration: 5000 });
    } catch (err) {
      console.error('Error envío masivo requisitos:', err);
      setBulkEmailError(err?.message || 'Error al enviar los correos');
      setBulkEmailStep('error');
      toast({ title: 'Error en envío masivo', status: 'error', duration: 5000 });
    } finally {
      setBulkEmailLoading(false);
    }
  }, [bulkEmailAsunto, bulkEmailMensajeInicial, bulkEmailMensajeFinal, filters, reqStatus, toast]);

  const totalStudents = report.meta?.summary?.total ?? 0;
  const canBulkEmail = Boolean(filters.program_type && filters.program && filters.group);

  const canAccessReport = ['supervisor_admin', 'admin'].includes(user?.role?.type);
  if (!canAccessReport) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          No tienes permisos para acceder a esta sección.
        </Text>
      </Box>
    );
  }

  const pagination = report.meta?.pagination || {};
  const summary = report.meta?.summary || {};
  const requirementColumns = report.meta?.requirements || [];
  const total = pagination.total || 0;
  const pageCount = pagination.pageCount || 0;
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <Box p={4} maxW="100%">
      <SmartFilterBar
        title="Filtro Requisitos"
        description="Selecciona los filtros para ver el estado de documentos por estudiante."
        value={filters}
        onChange={handleFilterChange}
        loading={loading}
      />

      {!hasMinimumFilters && (
        <Box
          mt={6}
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="gray.200"
          borderStyle="dashed"
          bg="gray.50"
          minH="280px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex flexDirection="column" align="center" gap={4} maxW="360px">
            <Flex
              w={16}
              h={16}
              borderRadius="full"
              bg="#E8E4FC"
              color="#574feb"
              borderWidth="1px"
              borderColor="#D4CCF0"
              align="center"
              justify="center"
            >
              <Box as={FiFilter} boxSize={8} />
            </Flex>
            <Box textAlign="center">
              <Text fontWeight="semibold" fontSize="lg" color="gray.800" mb={2}>
                Selecciona filtros para ver el reporte
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                Elige al menos un <strong>Tipo de programa</strong> para generar la matriz de requisitos por estudiante.
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {hasMinimumFilters && (
        <>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} my={6}>
            <Tooltip
              label="Cantidad total de estudiantes que coinciden con los filtros seleccionados."
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="blue.100" p={2} borderRadius="full">
                    <Box as={FiUsers} color="blue.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Total Estudiantes
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.total ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label="Estudiantes con todos los requisitos aprobados."
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="green.100" p={2} borderRadius="full">
                    <Box as={IoIosCheckmarkCircleOutline} color="green.600" boxSize={6} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Al Día
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.alDia ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label="Estudiantes con requisitos subidos pendientes de revisión, sin denegados."
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="orange.100" p={2} borderRadius="full">
                    <Box as={FiAlertCircle} color="orange.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Pendientes
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.pendientes ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip
              label="Estudiantes con requisitos faltantes (no han subido documentos), sin pendientes ni denegados."
              placement="top"
              hasArrow
            >
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="gray.100" p={2} borderRadius="full">
                    <Box as={FiMinusCircle} color="gray.500" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Incompletos
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.incompletos ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
            <Tooltip label="Estudiantes con al menos un requisito denegado." placement="top" hasArrow>
              <Box
                bg="white"
                p={4}
                borderRadius="lg"
                boxShadow="sm"
                borderWidth="1px"
                borderColor="gray.100"
                cursor="help"
              >
                <Flex align="center" gap={3}>
                  <Box bg="red.100" p={2} borderRadius="full">
                    <Box as={FiAlertTriangle} color="red.600" boxSize={5} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600">
                      Con Denegados
                    </Text>
                    <Text fontWeight="bold" fontSize="lg">
                      {summary.conDenegados ?? 0}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            </Tooltip>
          </SimpleGrid>

          <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
            <HStack spacing={4} fontSize="sm" color="gray.600" flexWrap="wrap">
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="green.200" />
                <Text>Aprobado</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="red.200" />
                <Text>Denegado</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="orange.200" />
                <Text>Pendiente</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="gray.300" />
                <Text>Faltante</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="purple.200" />
                <Text>Año anterior</Text>
              </HStack>
            </HStack>
            <HStack spacing={3} flexWrap="wrap" align="center">
              <Flex align="center" gap={2}>
                <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                  Estado:
                </Text>
                <Select
                  size="sm"
                  maxW="200px"
                  value={reqStatus}
                  onChange={handleReqStatusChange}
                  bg="white"
                >
                  {REQ_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Flex>
              <Button
                variant="outline"
                size="sm"
                borderColor={BRAND_BUTTON}
                color={BRAND_BUTTON}
                bg="white"
                _hover={{
                  bg: '#E8E4FC',
                  borderColor: BRAND_BUTTON,
                  color: BRAND_BUTTON,
                  _disabled: { opacity: 0.4, bg: 'white', borderColor: BRAND_BUTTON, cursor: 'not-allowed' },
                }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { opacity: 0.4, bg: 'white', borderColor: BRAND_BUTTON },
                }}
                leftIcon={<FiSend />}
                isDisabled={loading || !report.data.length || !canBulkEmail}
                onClick={handleBulkEmailOpen}
                title={!canBulkEmail ? 'Selecciona Tipo, Programa y Grupo para enviar correo masivo' : ''}
              >
                Enviar correo masivo
              </Button>
              <Button
                size="sm"
                colorScheme="green"
                isLoading={downloading}
                isDisabled={loading || !report.data.length}
                onClick={handleDownloadExcel}
              >
                Descargar Excel
              </Button>
            </HStack>
          </Flex>

          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderWidth="1px" borderColor="gray.100">
            <Box bg="#574feb" px={5} py={3}>
              <Flex align="center" gap={3}>
                <Box as={BiGridAlt} color="white" boxSize={6} />
                <Text fontWeight="bold" color="white" fontSize="lg">
                  Estado de requisitos por estudiante
                </Text>
              </Flex>
            </Box>

            {loading ? (
              <Flex justify="center" py={12}>
                <Spinner size="lg" />
              </Flex>
            ) : requirementColumns.length === 0 ? (
              <Box p={8} textAlign="center" color="gray.600">
                No hay requisitos configurados para este tipo de programa o programa seleccionado.
              </Box>
            ) : (
              <TableContainer overflowX="auto" maxW="100%">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th
                        position="sticky"
                        left={0}
                        zIndex={2}
                        bg="gray.50"
                        minW="220px"
                        borderRightWidth="1px"
                        py={3}
                        px={4}
                      >
                        ESTUDIANTE
                      </Th>
                      {requirementColumns.map((req) => (
                        <Th key={req.id} minW="140px" maxW="300px" textAlign="center" bg="gray.50" py={3} px={2}>
                          <Tooltip label={req.name} placement="top" hasArrow openDelay={300}>
                            <Text
                              fontSize="xs"
                              lineHeight="short"
                              whiteSpace="normal"
                              noOfLines={2}
                              cursor="default"
                              textTransform="capitalize"
                              fontWeight="semibold"
                            >
                              {req.name}
                            </Text>
                          </Tooltip>
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {!report.data.length ? (
                      <Tr>
                        <Td colSpan={1 + requirementColumns.length} textAlign="center" py={8} color="gray.500">
                          No hay estudiantes con los filtros seleccionados.
                        </Td>
                      </Tr>
                    ) : (
                    report.data.map((row) => {
                      const byId = {};
                      const prevYearById = {};
                      (row.requirements || []).forEach((x) => {
                        byId[x.requirementId] = x.status;
                        prevYearById[x.requirementId] = x.approvedPreviousYear || false;
                      });
                      return (
                        <Tr key={row.student?.id}>
                          <Td
                            position="sticky"
                            left={0}
                            zIndex={1}
                            bg="white"
                            borderRightWidth="1px"
                            borderColor="gray.100"
                          >
                            <Flex align="center" gap={3}>
                              <Flex
                                w={10}
                                h={10}
                                borderRadius="full"
                                bg="#E8E4FC"
                                color="#574feb"
                                borderWidth="1px"
                                borderColor="#D4CCF0"
                                align="center"
                                justify="center"
                                fontSize="xs"
                                fontWeight="bold"
                              >
                                {getInitials(row.student)}
                              </Flex>
                              <Box flex="1">
                                <Link
                                  href={`/plataforma/administrador/ver-estudiante/${row.student?.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  fontWeight="medium"
                                  color="blue.600"
                                  _hover={{ underline: true }}
                                >
                                  {getFullName(row.student)}
                                </Link>
                                <Text fontSize="xs" color="gray.500">
                                  ID: {row.student?.id ?? '—'}
                                </Text>
                              </Box>
                              <Tooltip label="Enviar correo de requisitos" placement="top" hasArrow>
                                <IconButton
                                  icon={<FiSend />}
                                  aria-label="Enviar correo"
                                  size="xs"
                                  variant="ghost"
                                  color="gray.500"
                                  _hover={{ color: BRAND_BUTTON, bg: '#E8E4FC' }}
                                  onClick={() => handleOpenEmail(row)}
                                />
                              </Tooltip>
                            </Flex>
                          </Td>
                          {requirementColumns.map((req) => (
                            <Td key={req.id} textAlign="center" minW="56px" px={1}>
                              <RequirementCell status={byId[req.id]} approvedPreviousYear={prevYearById[req.id]} />
                            </Td>
                          ))}
                        </Tr>
                      );
                    })
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            <Flex
              justify="space-between"
              align="center"
              p={4}
              borderTopWidth="1px"
              borderColor="gray.100"
              flexWrap="wrap"
              gap={3}
            >
              <Text color="gray.600" fontSize="sm">
                Mostrando {start}-{end} de {total} estudiantes
              </Text>
              {pageCount > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel=">"
                  previousLabel="<"
                  onPageChange={handlePageChange}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="reporteReqPagination"
                  pageClassName="reporteReqPaginationItem"
                  activeClassName="reporteReqPaginationActive"
                  previousClassName="reporteReqPaginationItem"
                  nextClassName="reporteReqPaginationItem"
                  disabledClassName="reporteReqPaginationDisabled"
                />
              )}
            </Flex>
          </Box>

          <style>{`
            .reporteReqPagination {
              display: flex;
              list-style: none;
              gap: 4px;
              margin: 0;
              padding: 0;
            }
            .reporteReqPaginationItem {
              display: flex;
              align-items: center;
              justify-content: center;
              min-width: 36px;
              height: 36px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              color: #4a5568;
              transition: all 0.2s;
            }
            .reporteReqPaginationItem:hover {
              background-color: #f7fafc;
              border-color: #cbd5e0;
            }
            .reporteReqPaginationItem a {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 100%;
              height: 100%;
              padding: 0 12px;
            }
            .reporteReqPaginationActive {
              background-color: #574feb !important;
              border-color: #574feb !important;
              color: white !important;
            }
            .reporteReqPaginationActive a {
              color: white !important;
            }
            .reporteReqPaginationDisabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
          `}</style>
        </>
      )}

      <Modal isOpen={isEmailOpen} onClose={handleCloseEmail} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh" h="90vh" display="flex" flexDirection="column">
          <ModalHeader>Enviar requisitos pendientes al acudiente</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6} flex="1" minH={0} display="flex" flexDirection="column">
            <Flex gap={6} flex="1" minH={0}>
              <Box flex="0 0 42%" overflowY="auto">
                <Text fontWeight="bold" mb={4} fontSize="md">
                  Información de envío
                </Text>
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Destinatario</FormLabel>
                  <Input
                    value={emailDestinatario}
                    size="sm"
                    placeholder="correo_del_acudiente@ejemplo.com"
                    onChange={(e) => setEmailDestinatario(e.target.value)}
                  />
                  {!emailDestinatario?.trim() && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      No se encontró correo del acudiente ni del estudiante. Escríbalo manualmente.
                    </Text>
                  )}
                </FormControl>
                <FormControl isRequired mb={4}>
                  <FormLabel fontSize="sm">Asunto</FormLabel>
                  <Input
                    value={emailAsunto}
                    onChange={(e) => {
                      setEmailAsunto(e.target.value);
                      scheduleEmailPreview();
                    }}
                    size="sm"
                    placeholder="Requisitos pendientes - Nombre del estudiante"
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel fontSize="sm">Mensaje inicial</FormLabel>
                  <Textarea
                    value={emailMensajeInicial}
                    onChange={(e) => {
                      setEmailMensajeInicial(e.target.value);
                      scheduleEmailPreview();
                    }}
                    size="sm"
                    rows={5}
                    placeholder="Escriba el mensaje que irá antes de la lista de requisitos..."
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel fontSize="sm">Mensaje final</FormLabel>
                  <Textarea
                    value={emailMensajeFinal}
                    onChange={(e) => {
                      setEmailMensajeFinal(e.target.value);
                      scheduleEmailPreview();
                    }}
                    size="sm"
                    rows={4}
                    placeholder="Escriba el mensaje de cierre..."
                  />
                </FormControl>
              </Box>
              <Box
                flex="1"
                minW={0}
                minH={0}
                display="flex"
                flexDirection="column"
                borderLeftWidth="1px"
                borderColor="gray.200"
                pl={6}
              >
                <Text fontWeight="bold" mb={4} fontSize="md">
                  Vista previa
                </Text>
                <Box
                  flex="1"
                  minH={0}
                  borderRadius="lg"
                  bg="gray.100"
                  pt={6}
                  px={6}
                  pb={0}
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  <Box
                    flex="1"
                    minH={0}
                    borderRadius="md"
                    bg="white"
                    boxShadow="sm"
                    overflowY="auto"
                    display="flex"
                    flexDirection="column"
                  >
                    {emailLoadingPreview ? (
                      <Flex justify="center" align="center" flex="1">
                        <Spinner size="lg" />
                      </Flex>
                    ) : emailPreviewHtml ? (
                      <Box flex="1" minH={0} position="relative">
                        <iframe
                          title="Vista previa del correo"
                          srcDoc={emailPreviewHtml}
                          sandbox=""
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
                        />
                      </Box>
                    ) : (
                      <Text color="gray.500" fontSize="sm" p={4}>
                        La vista previa se generará al cargar o al editar los mensajes.
                      </Text>
                    )}
                  </Box>
                </Box>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.100">
            <Button variant="ghost" mr={3} onClick={handleCloseEmail}>
              Cancelar
            </Button>
            <Button
              bg={BRAND_BUTTON}
              color="white"
              _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              onClick={handleSendEmail}
              isLoading={emailLoadingSend}
              isDisabled={!emailAsunto?.trim() || !emailDestinatario || emailLoadingSend}
            >
              Enviar correo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isBulkEmailOpen}
        onClose={bulkEmailStep === 'sending' ? () => {} : handleBulkEmailClose}
        isCentered
        size="lg"
        closeOnOverlayClick={bulkEmailStep !== 'sending'}
        closeOnEsc={bulkEmailStep !== 'sending'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {bulkEmailStep === 'configuring' &&
              `Enviar correo masivo (${totalStudents} estudiante${totalStudents === 1 ? '' : 's'})`}
            {bulkEmailStep === 'sending' && 'Enviando correos…'}
            {bulkEmailStep === 'success' && 'Envío completado'}
            {bulkEmailStep === 'error' && 'Error en el envío'}
          </ModalHeader>
          {bulkEmailStep !== 'sending' && <ModalCloseButton />}
          <ModalBody>
            {bulkEmailStep === 'configuring' && (
              <>
                <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3} mb={4}>
                  <Text fontSize="sm" color="gray.800">
                    <strong>Atención:</strong> se enviará un correo personalizado con los requisitos pendientes a cada
                    uno de los <strong>{totalStudents}</strong> estudiantes que coinciden con los filtros actuales.
                    Estudiantes que ya tienen todos los requisitos al día serán omitidos automáticamente.
                  </Text>
                </Box>
                <FormControl isRequired mb={3}>
                  <FormLabel fontSize="sm">Asunto del correo</FormLabel>
                  <Input
                    size="sm"
                    value={bulkEmailAsunto}
                    onChange={(e) => setBulkEmailAsunto(e.target.value)}
                    placeholder="Asunto"
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Mensaje personalizado (inicio)</FormLabel>
                  <Textarea
                    size="sm"
                    rows={6}
                    value={bulkEmailMensajeInicial}
                    onChange={(e) => setBulkEmailMensajeInicial(e.target.value)}
                  />
                </FormControl>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Mensaje de cierre</FormLabel>
                  <Textarea
                    size="sm"
                    rows={4}
                    value={bulkEmailMensajeFinal}
                    onChange={(e) => setBulkEmailMensajeFinal(e.target.value)}
                  />
                </FormControl>
                <Box bg="gray.100" borderRadius="md" p={3} fontSize="xs" color="gray.600">
                  <Text fontWeight="semibold" mb={2} color="gray.700">
                    Vista previa estructural del correo
                  </Text>
                  <Text>1. Logo / plantilla Klazia</Text>
                  <Text>2. Tu mensaje personalizado (inicio)</Text>
                  <Text>3. Bloque automático: tabla de requisitos pendientes/faltantes/denegados del estudiante</Text>
                  <Text>4. Mensaje de cierre y datos de contacto</Text>
                </Box>
              </>
            )}
            {bulkEmailStep === 'sending' && (
              <Flex direction="column" align="center" py={8} gap={4}>
                <Spinner size="xl" color={BRAND_BUTTON} thickness="4px" />
                <Text textAlign="center" color="gray.600">
                  Procesando envíos. Por favor, no cierres esta ventana…
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {totalStudents} estudiante{totalStudents === 1 ? '' : 's'}
                </Text>
              </Flex>
            )}
            {bulkEmailStep === 'success' && bulkEmailResult && (
              <Text fontSize="sm">
                Enviados: <strong>{bulkEmailResult.sent}</strong>.
                {(bulkEmailResult.skippedAllApproved ?? 0) > 0 && (
                  <>
                    {' '}
                    Omitidos (al día): <strong>{bulkEmailResult.skippedAllApproved}</strong>.
                  </>
                )}{' '}
                Sin correo de acudiente: <strong>{bulkEmailResult.noEmail}</strong>. Fallidos:{' '}
                <strong>{bulkEmailResult.failed}</strong>.
              </Text>
            )}
            {bulkEmailStep === 'error' && (
              <Text color="red.600" fontSize="sm">
                {bulkEmailError || 'No se pudo completar el envío.'}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            {bulkEmailStep === 'configuring' && (
              <>
                <Button variant="ghost" mr={3} onClick={handleBulkEmailClose}>
                  Cancelar
                </Button>
                <Button
                  bg={BRAND_BUTTON}
                  color="white"
                  _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }}
                  onClick={handleBulkEmailSend}
                  isDisabled={!bulkEmailAsunto?.trim()}
                >
                  Enviar a {totalStudents}
                </Button>
              </>
            )}
            {(bulkEmailStep === 'success' || bulkEmailStep === 'error') && (
              <Button bg={BRAND_BUTTON} color="white" _hover={{ bg: BRAND_BUTTON, opacity: 0.9 }} onClick={handleBulkEmailClose}>
                Cerrar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
